import { Button, ConfirmModal, ResetResultCourseModal, SecondButton } from 'UI';
import { SecondButtonVariant } from 'UI/SecondButton/SecondButton';
import api from 'api';
import {
  CourseTypeEnum,
  ManualTypeEnum,
  PaymentDtoLevelEnum,
  PaymentDtoStatusEnum,
  PaymentDtoTypeEnum,
} from 'api/generated';
import { UpgradeSubscriptionModal } from 'components';
import { routes } from 'constant/routes';
import { useAsyncAction, useClientSize, useNotifications, useToggle } from 'hooks';
import MainLayout from 'layouts/MainLayout';
import { Courses } from 'pages';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import styled, { css, useTheme } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';

import { AccountInfoCard, SettingButtonsList } from './components';

const Profile = () => {
  const { isOpen: isSignOutModalOpen, close: closeSignOutModalOpen, open: openSignOutModalOpen } = useToggle();

  const {
    isOpen: isConfirmResetCourseResultModalOpen,
    close: closeConfirmResetCourseResultModal,
    open: openConfirmResetCourseResultModalOpen,
  } = useToggle();

  const { isOpen: isOpenExamModal, close: closeExamModal, open: openExamModal } = useToggle();

  const [resetCourseResultsAction, isResetCourseResultsLoading] = useAsyncAction(actions.courses.resetCourseResults);
  const [getMyCourses, isCoursesLoading] = useAsyncAction(actions.courses.getMyCourses);
  const [getCourseStatisticAction] = useAsyncAction(actions.performance.getCourseStatistic);

  // ClassMarker integration
  const [registerForExamAction, isRegisteringForExam] = useAsyncAction(actions.classmarker.registerForExam);
  const [getTestStatusAction] = useAsyncAction(actions.classmarker.getTestStatus);
  const [getTestUrlAction] = useAsyncAction(actions.classmarker.getTestUrl);

  const isPause = useAppSelector(selectors.courses.selectIsPauseSubscription);
  const isPauseCertificate = useAppSelector(selectors.courses.selectIsPauseCertificate);
  const user = useAppSelector(selectors.user.selectUser);
  const direction = useAppSelector(selectors.direction.selectDirection);
  const activeCourse = useAppSelector(selectors.courses.selectActiveCourse);
  const courses = useAppSelector(selectors.courses.selectCourses);

  const hasCertOrBundle =
    courses?.some((course) => course.type === CourseTypeEnum.Certificate || course.type === CourseTypeEnum.Bundle) ||
    false;
  const coursesTitle = hasCertOrBundle ? 'Your Certifications' : 'Your Courses';

  // ClassMarker selectors
  const classMarkerError = useAppSelector(selectors.classmarker.selectError);
  const isLoadingTestUrl = useAppSelector(selectors.classmarker.selectIsLoadingUrl);
  const hasPassedExam = useAppSelector(selectors.classmarker.selectHasPassedExam);
  const examButtonText = useAppSelector(selectors.classmarker.selectExamButtonText);
  const hasLoadedTestStatus = useAppSelector(selectors.classmarker.selectHasLoadedTestStatus);
  const isLoadingStatus = useAppSelector(selectors.classmarker.selectIsLoadingStatus);

  const IPTA_CPT_COURSE_ID = '2b91e4ec-aed2-4542-b0ea-40d49d31bed5';
  const examCourse = activeCourse;

  const paymentType = examCourse?.currentPayment.type;
  const paymentStatus = examCourse?.currentPayment.status;
  const paymentLevel = examCourse?.currentPayment.level;

  const isPaidCourseType =
    paymentType === PaymentDtoTypeEnum.Installment || paymentType === PaymentDtoTypeEnum.Purchase;
  const hasInvitedLifetimeAccess =
    paymentType === PaymentDtoTypeEnum.Free && paymentLevel && paymentLevel !== PaymentDtoLevelEnum.None;
  const isPaidOrInvitedAccess = isPaidCourseType || hasInvitedLifetimeAccess;
  const isActivePaidStatus = !paymentStatus || paymentStatus === PaymentDtoStatusEnum.Active;
  const isActiveCourse = isPaidOrInvitedAccess && isActivePaidStatus;
  const isIptaCptCourse = examCourse?.id === IPTA_CPT_COURSE_ID;

  const classmarkerLinkId = (examCourse as { classmarkerLinkId?: string } | null)?.classmarkerLinkId;
  const classmarkerAccessListId = (examCourse as { classmarkerAccessListId?: string } | null)?.classmarkerAccessListId;
  const hasClassmarkerConfig = Boolean(classmarkerLinkId && classmarkerAccessListId);

  // Type-safe boolean checks
  const isLoadingExam = Boolean(isRegisteringForExam || isLoadingTestUrl);
  const canShowExamButton =
    isActiveCourse &&
    hasClassmarkerConfig &&
    !isIptaCptCourse &&
    !hasPassedExam &&
    !isLoadingStatus &&
    (hasLoadedTestStatus || Boolean(classMarkerError));

  const { colors } = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { errorToast, successToast } = useNotifications();
  const { boot, show, shutdown } = useIntercom();

  const { getIsBreakpoint } = useClientSize();
  const isWidthLs = getIsBreakpoint('ls');

  const handleOpenBillingPage = () => {
    navigate(routes.billing);
  };

  const openSupport = useCallback(async () => {
    try {
      const { data: userHash } = await api.Customer.customersControllerCreateUserIntercomHash();

      boot({
        // eslint-disable-next-line camelcase
        customAttributes: { custom_attribute_key: 'hi there' },
        company: { companyId: 'trainer', name: 'trainer' },
        actionColor: colors.primary[1],
        backgroundColor: colors.primary[1],
        name: user?.name,
        email: user?.email,
        userId: user?.id,
        userHash,
        utmSource: direction?.name,
        hideDefaultLauncher: true,
      });
      show();
    } catch {
      errorToast('Something went wrong');
    }
  }, [colors, user, direction]);

  const signOut = () => {
    shutdown();
    dispatch(actions.auth.signOut());
    localStorage.removeItem('hasSeenOnboarding');
    navigate(routes.signIn);
  };

  const resetCourseResults = async () => {
    try {
      await resetCourseResultsAction();
      if (!isPause && !isPauseCertificate) {
        await getCourseStatisticAction();
      }

      closeConfirmResetCourseResultModal();
      successToast('Your course results successfully reset');
    } catch (error) {
      errorToast('Something went wrong');
    }
  };

  // ClassMarker handlers
  const handleSignUpForExam = useCallback(async () => {
    if (!examCourse?.id || !hasClassmarkerConfig || isIptaCptCourse) return;

    try {
      // Always try to register first, then get URL
      await registerForExamAction({ courseId: examCourse.id });

      // Get test URL and redirect
      const result = await getTestUrlAction({ courseId: examCourse.id });

      // The result is the payload directly from the fulfilled thunk
      if (result?.testUrl) {
        window.open(result.testUrl, '_blank');
        successToast('Opening exam...');
      } else {
        errorToast('Failed to get exam URL. Please try again.');
      }
    } catch (error) {
      const errorMessage =
        typeof classMarkerError === 'string' ? classMarkerError : 'Failed to start exam. Please try again.';
      errorToast(errorMessage);
    }
  }, [
    examCourse?.id,
    hasClassmarkerConfig,
    isIptaCptCourse,
    registerForExamAction,
    getTestUrlAction,
    classMarkerError,
    successToast,
    errorToast,
  ]);
  const lastStatusCourseIdRef = useRef<string | null>(null);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        await getMyCourses();
      } catch (error) {
        console.log('Error loading courses:', error);
      }
    };

    void loadCourses();
  }, []);

  useEffect(() => {
    if (!hasClassmarkerConfig || isIptaCptCourse || !examCourse?.id) return;
    if (lastStatusCourseIdRef.current === examCourse.id) return;

    lastStatusCourseIdRef.current = examCourse.id;
    getTestStatusAction({ courseId: examCourse.id });
  }, [hasClassmarkerConfig, isIptaCptCourse, examCourse?.id, getTestStatusAction]);

  return (
    <StyledMainLayout>
      <Root>
        <Title>Profile</Title>

        <Container>
          <InfoContainer>
            <InfoWrapper>
              <AccountInfoCard />

              {isActiveCourse && hasClassmarkerConfig && !isIptaCptCourse && isLoadingStatus && (
                <StyledButton size="small" disabled>
                  Loading...
                </StyledButton>
              )}

              {canShowExamButton && (
                <StyledButton size="small" onClick={handleSignUpForExam} disabled={isLoadingExam}>
                  {examButtonText}
                </StyledButton>
              )}

              {isActiveCourse && hasClassmarkerConfig && !isIptaCptCourse && hasLoadedTestStatus && hasPassedExam && (
                <PassedExamButton size="small" disabled>
                  Exam Completed ✓
                </PassedExamButton>
              )}

              {isActiveCourse && isIptaCptCourse && (
                <StyledButton size="small" onClick={openExamModal}>
                  Sign up for the exam
                </StyledButton>
              )}

              {isWidthLs && (
                <Courses isLoading={isCoursesLoading} title={coursesTitle} isShowChangePlanButton={false} />
              )}

              <ButtonsContainer>
                <SettingButtonsList
                  openConfirmResetCourseResultModalOpen={openConfirmResetCourseResultModalOpen}
                  isResetCourseResultsLoading={isResetCourseResultsLoading}
                />

                <SecondButton variant={SecondButtonVariant.SECONDARY} text="Support" onClick={openSupport} />
                <SecondButton variant={SecondButtonVariant.SECONDARY} text="Billing" onClick={handleOpenBillingPage} />
              </ButtonsContainer>
            </InfoWrapper>

            <ButtonsContainer>
              <SecondButton onClick={openSignOutModalOpen} text="Sign out" />
            </ButtonsContainer>
          </InfoContainer>

          {!isWidthLs && <Courses isLoading={isCoursesLoading} title={coursesTitle} isShowChangePlanButton={false} />}
          <ConfirmModal
            title="Are you sure you want to sign out?"
            isOpen={isSignOutModalOpen}
            onClose={closeSignOutModalOpen}
            onLeftButtonClick={signOut}
            onRightButtonClick={closeSignOutModalOpen}
          />

          <ResetResultCourseModal
            courseName={activeCourse?.name || ''}
            isOpen={isConfirmResetCourseResultModalOpen}
            onClose={closeConfirmResetCourseResultModal}
            onLeftButtonClick={resetCourseResults}
            onRightButtonClick={closeConfirmResetCourseResultModal}
            isLoading={isResetCourseResultsLoading}
          />

          <UpgradeSubscriptionModal
            isOpen={isOpenExamModal}
            onClose={closeExamModal}
            manualType={ManualTypeEnum.CertificateSignup}
            isBonusModal={true}
          />
        </Container>
      </Root>
    </StyledMainLayout>
  );
};

export default Profile;

const StyledMainLayout = styled(MainLayout)`
  ${respondToWidth.sm`
    width: 100%;
  `}
`;

const Root = styled.div`
  ${({ theme: { colors } }) => css`
    height: 100%;

    padding: 28px 40px 40px 0;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;

    ${respondToWidth.sm`
      display: block;
      padding: 16px;
      padding-bottom: 125px;
      overflow: hidden;
      background-color: ${colors.neutrals[11]};
    `}
  `}
`;

const StyledButton = styled(Button)`
  width: 100%;
  padding: 10px 0px;
`;

const PassedExamButton = styled(Button)`
  width: 100%;
  padding: 10px 0px;

  &:disabled {
    background-color: #ff8c00 !important; /* Orange color */
    color: white !important;
    border: none !important;
    opacity: 1 !important;
    cursor: default !important;

    &:hover {
      background-color: #ff8c00 !important;
      transform: none !important;
    }
  }
`;

const Title = styled.h1`
  ${({ theme: { typography, colors } }) => css`
    ${typography.title_2_bold_32}
    color: ${colors.neutrals[1]};
  `}

  display: flex;
  margin-bottom: 20px;
  width: 100%;
`;

const Container = styled.div`
  ${({ theme: { colors } }) => css`
    display: grid;
    grid-template-columns: 288px 1fr;
    grid-gap: 24px;

    ${respondToWidth.ls`
    grid-template-columns: 1fr;
  `}

    ${respondToWidth.sm`
      display: block;
      padding: 16px;
      padding-bottom: 125px;
      overflow: hidden;
      background-color: ${colors.neutrals[11]};

    `}
  `}
`;

const InfoContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  justify-content: space-between;
  gap: 24px;

  ${respondToWidth.sm`
      gap: 32px;
  `}
`;

const InfoWrapper = styled.div`
  ${({ theme: { colors } }) => css`
    display: flex;
    flex-direction: column;

    gap: 24px;

    ${respondToWidth.sm`
      gap: 32px;
      padding-bottom: 32px;

      border-bottom: 1px solid ${colors.neutrals[9]};
    `}
  `}
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;

  gap: 24px;

  ${respondToWidth.sm`
      gap: 16px;
    `}
`;
